import PageWithBreadcrumbs from '@/components/page-with-breadcrumbs';
import { Button } from '@/components/ui/button';
import { Spinner } from '@/components/ui/spinner';
import { api, type ApiSchema } from '@/lib/api';
import { useTranslation } from '@/lib/i18n';
import { permissaoGuard } from '@/lib/permissions';
import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';
import ErrorOutline from '~icons/material-symbols/error-outline';

type MaterialGraficoItem =
  ApiSchema<'MateriaisGraficos.MaterialGraficoItemDTO'>;

export const Route = createFileRoute(
  '/__authenticated/comercial/produto/material-grafico',
)({
  beforeLoad: async () => {
    await permissaoGuard('MaterialGrafico');
  },
  component: RouteComponent,
});

function ItemMaterial({ item }: { item: MaterialGraficoItem }) {
  const [miniaturaInvalida, setMiniaturaInvalida] = useState(false);

  const { t } = useTranslation('private');

  const miniatura = item.miniatura?.trim();

  return (
    <figure className="flex flex-col gap-3">
      {miniatura && !miniaturaInvalida && (
        <img
          src={miniatura}
          alt={item.descricao}
          className="h-18 w-full rounded object-contain"
          onError={() => {
            setMiniaturaInvalida(true);
          }}
        />
      )}
      <figcaption className="flex-1 text-center text-sm font-light text-gray-500">
        {item.descricao}
      </figcaption>
      <Button asChild size="sm" className="w-full">
        <a href={item.link} target="_blank" rel="noopener noreferrer">
          {t($ => $.materialGrafico.acessar)}
        </a>
      </Button>
    </figure>
  );
}

function ListaMateriais() {
  const { t } = useTranslation('private');

  const {
    data: grupos,
    isLoading,
    isError,
  } = api.useQuery('get', '/materiais-graficos');

  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <Spinner className="size-8" />
      </div>
    );
  }

  if (isError || !grupos) {
    return (
      <div className="flex items-center gap-2 text-destructive">
        <ErrorOutline className="size-5 shrink-0" />
        <p>{t($ => $.materialGrafico.erroCarregamento)}</p>
      </div>
    );
  }

  if (grupos.length === 0) {
    return (
      <p className="text-muted-foreground">
        {t($ => $.materialGrafico.semItens)}
      </p>
    );
  }

  return (
    <div className="flex flex-col gap-6">
      {grupos.map(grupo => (
        <section
          key={grupo.grupo}
          className="rounded-xl border border-gray-800/20 p-6"
        >
          <h2 className="mb-6 text-lg font-medium text-gray-500">
            {grupo.grupo}
          </h2>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {grupo.itens?.map(item => (
              <ItemMaterial key={item.link} item={item} />
            ))}
          </div>
        </section>
      ))}
    </div>
  );
}

function RouteComponent() {
  const { t } = useTranslation('private');

  return (
    <PageWithBreadcrumbs
      breadcrumbs={[
        t($ => $.navbar.comercial),
        t($ => $.navbar.produto),
        t($ => $.navbar.materialGrafico),
      ]}
    >
      <div className="flex flex-col gap-2">
        <h1 className="text-3xl font-medium text-gray-600">
          {t($ => $.materialGrafico.titulo)}
        </h1>
        <div className="flex flex-col gap-1 text-sm font-normal text-gray-500">
          <p>{t($ => $.materialGrafico.descricao)}</p>
          <p>{t($ => $.materialGrafico.duvidas)}</p>
        </div>
      </div>

      <ListaMateriais />
    </PageWithBreadcrumbs>
  );
}
